import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type DocumentListResponse } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import {
  Avatar,
  CategoryChip,
  ChipToggle,
  Empty,
  ErrorNote,
  PageHeader,
  ReviewBadge,
  Spinner,
  StatusPill,
  useToast,
} from "../../components/ui";

const SORTS = [
  { key: "title", label: "Name" },
  { key: "status", label: "Status" },
  { key: "version", label: "Version" },
  { key: "created", label: "Created" },
  { key: "owner", label: "Owner" },
  { key: "category", label: "Category" },
] as const;

export function DocumentListScreen() {
  const user = useUser();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const view = params.get("view") ?? "";
  const page = Number(params.get("page") ?? 1);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<string>("created");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["documents", view, page, filter, sort, direction],
    queryFn: () =>
      api.get<DocumentListResponse>("/documents", {
        view: view || undefined,
        q: filter || undefined,
        page,
        pageSize: 6,
        sort,
        direction,
      }),
  });

  const setPage = (next: number) => {
    const updated = new URLSearchParams(params);
    updated.set("page", String(next));
    setParams(updated);
    setSelected(new Set());
  };

  const toggleSort = (key: string) => {
    if (sort === key) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDirection("asc");
    }
  };

  const toggleRow = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const items = data?.items ?? [];
  const allOnPageSelected =
    items.length > 0 && items.every((item) => selected.has(item.id));

  const bulkPublish = async () => {
    // Bulk actions go through the same transition endpoint as a single
    // document, one call each, so the engine validates every one of them.
    let published = 0;
    let refused = 0;
    for (const id of selected) {
      try {
        await api.post(`/workflow/documents/${id}/transition`, {
          action: "APPROVE_ONLINE",
        });
        published += 1;
      } catch {
        refused += 1;
      }
    }
    setSelected(new Set());
    void refetch();
    toast(
      refused
        ? `${published} published, ${refused} could not be (not awaiting approval)`
        : `${published} document(s) published`,
      refused ? "danger" : "info",
    );
  };

  const activeView = data?.views.find((v) => v.key === view);

  return (
    <>
      <PageHeader
        eyebrow="Library"
        title={activeView?.label ?? "Document List"}
        subtitle="Synced from the document library, which remains the system of record"
        actions={
          <>
            {user.canUpload && (
              <Link to="/ingestion" className="btn-ghost no-underline">
                + New document
              </Link>
            )}
            <a
              href={`${api.baseUrl}/api/v1/documents/export${view ? `?view=${view}` : ""}`}
              className="btn-ghost no-underline"
              onClick={(event) => {
                // The export endpoint needs the bearer token, which a plain
                // link cannot send. Fetch it and hand the browser a blob.
                event.preventDefault();
                void api
                  .blob("/documents/export", { view: view || undefined })
                  .then((url) => {
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = "compendium.csv";
                    anchor.click();
                    URL.revokeObjectURL(url);
                    toast("Export downloaded");
                  })
                  .catch(() => toast("Could not export", "danger"));
              }}
            >
              Export view
            </a>
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {data?.views.map((entry) => (
          <ChipToggle
            key={entry.key}
            label={entry.label}
            count={entry.count}
            dotColour={entry.dotColour}
            active={(view || "all_items") === entry.key}
            onClick={() => {
              const updated = new URLSearchParams();
              if (entry.key !== "all_items") updated.set("view", entry.key);
              setParams(updated);
            }}
          />
        ))}
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by title, reference or owner"
          aria-label="Filter documents"
          className="field-toolbar ml-auto max-w-[240px]"
        />
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface px-3 py-2">
          <span className="text-sm font-bold text-ink">
            {selected.size} selected
          </span>
          {user.canPublish && (
            <button className="btn-accent" onClick={() => void bulkPublish()}>
              Publish
            </button>
          )}
          <button className="btn-quiet" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      {data && items.length === 0 && (
        <div className="card">
          <Empty
            title="No documents in this view"
            detail={
              filter
                ? "Nothing matches that filter."
                : user.canSeeDrafts
                  ? "Load the knowledge base from the Ingestion screen to populate the library."
                  : "Only published documents are visible to your role."
            }
          />
        </div>
      )}

      {data && items.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left">
              <thead>
                <tr className="table-head">
                  <th className="w-9 px-4 py-2.5">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-ink"
                      aria-label="Select all on this page"
                      checked={allOnPageSelected}
                      onChange={() =>
                        setSelected(
                          allOnPageSelected
                            ? new Set()
                            : new Set(items.map((item) => item.id)),
                        )
                      }
                    />
                  </th>
                  {SORTS.map((column) => (
                    <th key={column.key} className="px-3 py-2.5">
                      <button
                        onClick={() => toggleSort(column.key)}
                        className="inline-flex items-center gap-1 hover:text-ink"
                      >
                        {column.label}
                        {sort === column.key && (
                          <span aria-hidden>{direction === "asc" ? "▲" : "▼"}</span>
                        )}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((document) => (
                  <tr
                    key={document.id}
                    className="border-b border-line-soft last:border-0 hover:bg-surface-hover"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-ink"
                        aria-label={`Select ${document.title}`}
                        checked={selected.has(document.id)}
                        onChange={() => toggleRow(document.id)}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <Link
                        to={`/documents/${document.id}`}
                        className="block no-underline"
                      >
                        <span className="block text-base font-bold text-ink">
                          {document.title}
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-2xs text-muted-soft">
                          {document.docNo}
                          {document.lockedBy && (
                            <span className="text-[#B23438]">
                              · checked out by {document.lockedBy}
                            </span>
                          )}
                          {document.hasConflict && (
                            <span className="text-[#B23438]">· changed at source</span>
                          )}
                        </span>
                      </Link>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusPill
                          status={document.status}
                          label={document.statusLabel}
                        />
                        <ReviewBadge
                          state={document.reviewState}
                          days={document.daysUntilReview}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-muted-strong">
                      {document.versionLabel}
                    </td>
                    <td className="px-3 py-3 text-sm text-muted-strong">
                      {formatDate(document.createdAt)}
                    </td>
                    <td className="px-3 py-3">
                      {document.owner ? (
                        <span className="flex items-center gap-2">
                          <Avatar
                            initials={document.owner.initials}
                            colour={document.owner.avatarColour}
                            size={22}
                          />
                          <span className="text-sm text-muted-strong">
                            {document.owner.displayName}
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm text-muted-soft">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <CategoryChip label={document.categoryLabel} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-line-soft px-4 py-2.5 text-sm text-muted">
            <span>
              Showing {(data.page - 1) * data.pageSize + 1}–
              {Math.min(data.page * data.pageSize, data.total)} of {data.total}
            </span>
            <span className="flex items-center gap-2">
              <button
                className="btn-ghost !py-1"
                disabled={data.page <= 1}
                onClick={() => setPage(data.page - 1)}
              >
                Previous
              </button>
              <span className="text-2xs font-bold text-muted-strong">
                Page {data.page} of {Math.max(data.pageCount, 1)}
              </span>
              <button
                className="btn-ghost !py-1"
                disabled={data.page >= data.pageCount}
                onClick={() => setPage(data.page + 1)}
              >
                Next
              </button>
            </span>
          </div>
        </div>
      )}
    </>
  );
}
